/**
 * islandModel — pure logic for the island panel: filters, counts, and the
 * keyboard nav reducer. Rows arrive law-sorted from deriveFleet (error >
 * needs > working > done); filtering preserves that order.
 */
import type { FleetRow, FleetState } from "./fleet";

export type IslandFilter = "all" | "attn" | "working" | "done";

export const FILTERS: Array<{ id: IslandFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "attn", label: "Needs you" },
  { id: "working", label: "Working" },
  { id: "done", label: "Done" },
];

export const CHIP: Record<
  FleetState,
  { word: string; cls: string; bcls: string }
> = {
  needs: {
    word: "NEEDS YOU",
    cls: "text-[var(--cv2-chip-needs)]",
    bcls: "border-[var(--cv2-chip-needs)]",
  },
  working: {
    word: "LIVE",
    cls: "text-[var(--cv2-chip-live)]",
    bcls: "border-[var(--cv2-chip-live)]",
  },
  done: {
    word: "DONE",
    cls: "text-[var(--cv2-chip-done)]",
    bcls: "border-[var(--cv2-chip-done)]",
  },
};

export function matches(state: FleetState, filter: IslandFilter): boolean {
  if (filter === "all") return true;
  if (filter === "attn") return state === "needs";
  return state === filter;
}

export function visibleRows(
  rows: FleetRow[],
  filter: IslandFilter
): FleetRow[] {
  return rows.filter((r) => matches(r.state, filter));
}

export function filterCounts(rows: FleetRow[]): Record<IslandFilter, number> {
  return {
    all: rows.length,
    attn: rows.filter((r) => r.state === "needs").length,
    working: rows.filter((r) => r.state === "working").length,
    done: rows.filter((r) => r.state === "done").length,
  };
}

export interface IslandNav {
  selIx: number;
  filter: IslandFilter;
}

/**
 * One keyboard step: ↑↓ wrap the selection, ←→ cycle the filter (selection
 * resets). Returns null for keys the island does not own (Enter is handled by
 * the component — it needs row context to fire the primary action).
 */
export function keyboardStep(
  nav: IslandNav,
  key: string,
  visibleCount: number
): IslandNav | null {
  if (key === "ArrowDown" || key === "ArrowUp") {
    if (visibleCount === 0) return nav;
    const delta = key === "ArrowDown" ? 1 : -1;
    return { ...nav, selIx: (nav.selIx + delta + visibleCount) % visibleCount };
  }
  if (key === "ArrowLeft" || key === "ArrowRight") {
    const ix = FILTERS.findIndex(({ id }) => id === nav.filter);
    const delta = key === "ArrowRight" ? 1 : -1;
    const next = FILTERS[(ix + delta + FILTERS.length) % FILTERS.length];
    return { selIx: 0, filter: next ? next.id : "all" };
  }
  return null;
}
